import JsBarcode from 'jsbarcode';
import React, {
    Dispatch,
    Fragment,
    FunctionComponent,
    SetStateAction,
    useContext,
    useEffect,
    useRef,
    useState,
} from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Store } from '../../../StoreFinder/Api/StoresApi';
import { StoreFinderComponent } from '../../../StoreFinder/StoreFinderComponent';
import { Floor } from '../../Api/FloorsApi';
import { sendShoppingListLink } from '../../Api/ShareApi';
import { BaseLayout } from '../../Component/BaseLayout/BaseLayout';
import { SmallButton } from '../../Component/SmallButton/SmallButton';
import { centsToEuroString } from '../../Component/Formatter/Currency';
import { Icon } from '../../Component/Icon/Icon';
import { ShoppingPlannerContext } from '../../Context/ShoppingPlannerContext';
import { useProductsFromStates } from '../../Hook/UseProductsFromStates';
import { useShoppingList } from '../../Hook/UseShoppingList';
import { Button } from '../../Component/Button/Button';
import { urls } from '../../Constants/Urls';

import './ShoppingList.scss';

interface ShoppingListProps {}

export const ShoppingList: FunctionComponent<ShoppingListProps> = (props) => {
    const { sendUserInputToServer, slug, setSlug, selectedInstallationAndCare } = useContext(ShoppingPlannerContext);

    const { floor, decor, insulation, skirtingDecor, skirting, installationAndCare, sqm } = useProductsFromStates();
    const navigate = useNavigate();

    // FIXME: Does not feel right do make this very important state change "in the middle" of a component body
    // Would be a better fit for Context, but we can only access params within Route component children.
    const { slugFromUrl } = useParams<{ slugFromUrl?: string }>();

    const [store, setStore] = useState<Store | null>(null);
    const [storeOverlay, setStoreOverlay] = useState(false);

    useEffect(() => {
        if (slugFromUrl) {
            setSlug(slugFromUrl);
        } else if (!slug) {
            sendUserInputToServer();
        }
    }, []);

    useEffect(() => {
        if (slug) {
            // make sure slug is always append to URL so
            // sharing the link is even easier
            navigate(urls.steps.shoppingList.builder(slug));
        }
    }, [slug]);

    const {
        decorPackageAmount,
        decorPrice,
        skirtingPackageAmount,
        skirtingPrice,
        insulationPackageAmount,
        insulationPrice,
        cornersAndCaps,
        total,
    } = useShoppingList();

    const shortLink = window.location.hostname + '/e/' + slug;

    const printButton = (
        <Button onClick={() => window.print()} className="shopping-planner-shopping-list__print-button">
            Drucken
            <Icon icon="print" />
        </Button>
    );

    const accessoriesCount = (insulation ? 1 : 0) + (skirting ? 1 : 0) + installationAndCare.length + 1;

    const [mailSent, setMailSent] = useState<'success' | 'failure' | undefined>(undefined);
    const [recipient, setRecipient] = useState<string>('');

    return (
        <BaseLayout hideInfoBar>
            <div className="shopping-planner-shopping-list-container">
                <div className="shopping-planner-shopping-list-container__print-button-container">{printButton}</div>
                <div className="shopping-planner-shopping-list-container__grid">
                    <h1 className="shopping-planner-shopping-list-container__headline">Einkaufszettel</h1>
                    <div className="shopping-planner-shopping-list">
                        <table className="shopping-planner-shopping-list__table">
                            <tbody>
                                <tr>
                                    <th>Raum</th>
                                    <td>
                                        <span className="shopping-planner-shopping-list__label">Raumgröße</span>
                                    </td>
                                    <td className="shopping-planner-shopping-list__value">{sqm} m²</td>
                                    <td colSpan={2}></td>
                                </tr>
                                {decor && floor ? (
                                    <tr>
                                        <th>Boden</th>
                                        <td>
                                            <img
                                                className="shopping-planner-shopping-list__decor-image"
                                                src={decor.imageUri ?? ''}
                                            />
                                        </td>
                                        <td className="td td--align-bottom">
                                            <div>
                                                <span
                                                    className={[
                                                        'shopping-planner-shopping-list__label shopping-planner-shopping-list__label--floor-family',
                                                    ].join(' ')}
                                                >
                                                    {floor.floorFamilyLabel}
                                                </span>
                                                <FloorLabelIfNecessary floor={floor} />
                                                <span className="shopping-planner-shopping-list__label shopping-planner-shopping-list__label--decor">
                                                    {decor.label}
                                                </span>
                                            </div>
                                            {decorPackageAmount} Pakete à{' '}
                                            {Intl.NumberFormat('de-DE').format(decor.squareMetersPerPackage)}m²
                                        </td>
                                        <td className="td td--align-bottom shopping-planner-shopping-list__bar-code-column">
                                            <BarCode ean={decor.ean} />
                                        </td>
                                        <td className="td td--price">{centsToEuroString(decorPrice)}</td>
                                    </tr>
                                ) : null}

                                {insulation || skirtingDecor || installationAndCare ? (
                                    <tr>
                                        <th rowSpan={accessoriesCount}>Zubehör</th>
                                    </tr>
                                ) : null}

                                {insulation ? (
                                    <tr>
                                        <td>
                                            <span className="shopping-planner-shopping-list__label">Dämmung</span>
                                        </td>
                                        <td className="td td--full-width">
                                            <span className="shopping-planner-shopping-list__value">
                                                {insulation.label}
                                            </span>
                                            {insulationPackageAmount} Pakete à{' '}
                                            {Intl.NumberFormat('de-DE').format(insulation.squareMetersPerPackage)} m²
                                        </td>
                                        <td className="shopping-planner-shopping-list__bar-code-column">
                                            <BarCode ean={insulation.ean} />
                                        </td>
                                        <td className="td td--price">{centsToEuroString(insulationPrice)}</td>
                                    </tr>
                                ) : null}
                                {skirtingDecor ? (
                                    <tr>
                                        <td>
                                            <span className="shopping-planner-shopping-list__label">Leisten</span>
                                        </td>
                                        <td className="td td--full-width shopping-planner-shopping-list__skirting-column">
                                            <div>
                                                <span className="shopping-planner-shopping-list__value">
                                                    {skirting?.label + ' (' + skirtingDecor.label + ')'} <br />
                                                </span>
                                                {skirting && skirtingPackageAmount ? (
                                                    <>
                                                        {skirtingPackageAmount} Leisten à{' '}
                                                        {Intl.NumberFormat('de-DE').format(
                                                            skirting?.dimensions.length / 1000
                                                        )}{' '}
                                                        m
                                                    </>
                                                ) : null}
                                                <div>{cornersAndCaps.join(', ')}</div>
                                            </div>
                                        </td>
                                        <td className="shopping-planner-shopping-list__bar-code-column">
                                            <BarCode ean={skirtingDecor.ean} />
                                        </td>
                                        <td className="td td--price">
                                            {skirtingPrice > 0 ? centsToEuroString(skirtingPrice) : null}
                                        </td>
                                    </tr>
                                ) : null}

                                {installationAndCare.length > 0
                                    ? installationAndCare.map((installationAndCareProduct) => (
                                          <tr key={installationAndCareProduct.accessoryId}>
                                              <td>
                                                  <span className="shopping-planner-shopping-list__label">
                                                      {installationAndCareProduct.label}
                                                      {installationAndCareProduct.variants.length < 2
                                                          ? null
                                                          : installationAndCareProduct.variants
                                                                .filter((variant) =>
                                                                    selectedInstallationAndCare.includes(
                                                                        variant.articleNumber
                                                                    )
                                                                )
                                                                .filter(
                                                                    (variant) =>
                                                                        variant.label !==
                                                                        installationAndCareProduct.label
                                                                )
                                                                .map((variant) => (
                                                                    <Fragment key={variant.articleNumber}>
                                                                        <br /> {variant.label} <br />
                                                                    </Fragment>
                                                                ))}
                                                  </span>
                                              </td>
                                              <td className="td td--full-width"></td>
                                              <td className="shopping-planner-shopping-list__bar-code-column">
                                                  {installationAndCareProduct.variants
                                                      .filter((variant) =>
                                                          selectedInstallationAndCare.includes(variant.articleNumber)
                                                      )
                                                      .map((variant) => (
                                                          <Fragment key={variant.ean}>
                                                              <BarCode ean={variant.ean} />
                                                          </Fragment>
                                                      ))}
                                              </td>

                                              <td className="td td--price">
                                                  {centsToEuroString(
                                                      installationAndCareProduct.variants
                                                          .filter((variant) =>
                                                              selectedInstallationAndCare.includes(
                                                                  variant.articleNumber
                                                              )
                                                          )
                                                          .reduce((acc, variant) => variant.pricePerPiece + acc, 0)
                                                  )}
                                              </td>
                                          </tr>
                                      ))
                                    : null}
                            </tbody>
                            <tfoot>
                                <tr>
                                    <th>Gesamt</th>
                                    <td></td>
                                    <td></td>
                                    <td className="td td--full-width"></td>
                                    <td className="td td--price">
                                        <strong>{centsToEuroString(total)}</strong>
                                    </td>
                                </tr>
                                <tr>
                                    <td colSpan={100} className="shopping-planner-shopping-list__disclaimer">
                                        <small>
                                            * Die angegebenen Preise sind nur in Deutschland gültig. <br />
                                            ** Alle Angaben ohne Gewähr.
                                        </small>
                                    </td>
                                </tr>
                            </tfoot>
                        </table>

                        <table className="shopping-planner-shopping-list__table shopping-planner-shopping-list__table--store-finder">
                            <tbody>
                                <tr>
                                    <th>Filiale</th>
                                    <td>
                                        <span className="shopping-planner-shopping-list__label">
                                            Bauhaus Fachcentrum
                                        </span>
                                    </td>
                                    <td colSpan={2}>
                                        {store ? (
                                            <>
                                                {store.street} <br /> {store.postcode} {store.city} <br />
                                                Tel.: <a href={'tel:' + store.phoneNumber}>{store.phoneNumber}</a>
                                            </>
                                        ) : null}
                                    </td>
                                    <td className="td td--price">
                                        <button
                                            className="storefinder-overlay-button"
                                            onClick={() => setStoreOverlay(true)}
                                        >
                                            <Icon icon="pin" />
                                            Standort wählen
                                        </button>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
                <footer className="shopping-planner-shopping-list__footer">
                    <div className="shopping-planner-shopping-list__footer-item">
                        <label>
                            <div className="shopping-planner-shopping-list__label-with-icon">
                                <div className="shopping-planner-shopping-list__label">Link zum Einkaufszettel</div>
                            </div>

                            <input readOnly={true} value={shortLink} />
                        </label>
                        {/* will only work within a secure origin */}
                        <Button onClick={() => navigator.clipboard.writeText(shortLink)}>Kopieren</Button>
                    </div>
                    <div className="shopping-planner-shopping-list__footer-item">
                        {mailSent === undefined ? (
                            <form
                                onSubmit={(event) => {
                                    event.preventDefault();
                                    if (slug) {
                                        sendShoppingListLink(slug, recipient, window.__GGLocale).then((response) =>
                                            response.success === true ? setMailSent('success') : setMailSent('failure')
                                        );
                                    }
                                }}
                            >
                                <label>
                                    <div className="shopping-planner-shopping-list__label-with-icon">
                                        <div className="shopping-planner-shopping-list__label">
                                            Als E-Mail versenden
                                        </div>
                                    </div>
                                    <input
                                        placeholder="E-Mail Adresse"
                                        value={recipient}
                                        onChange={(event) => setRecipient(event.currentTarget.value)}
                                    />
                                </label>
                                <Button type="submit" icon="arrow">
                                    Senden
                                </Button>
                            </form>
                        ) : mailSent === 'success' ? (
                            <div>
                                <p>Der Link wurde verschickt</p>
                                <SmallButton onClick={() => setMailSent(undefined)}>
                                    weiteren Link verschicken
                                </SmallButton>
                            </div>
                        ) : mailSent === 'failure' ? (
                            <div>
                                <p>Der Link konnte nicht verschickt werden</p>
                                <Button onClick={() => setMailSent(undefined)}>weiteren Link verschicken</Button>
                            </div>
                        ) : null}
                    </div>
                    {printButton}
                </footer>
            </div>
            {storeOverlay ? (
                <StoreFinderModal
                    setStoreOverlay={setStoreOverlay}
                    onMarkerClick={(store: Store) => {
                        setStore(store);
                        setStoreOverlay(false);
                    }}
                />
            ) : null}
        </BaseLayout>
    );
};

interface FloorLabelIfNecessaryProps {
    floor: Floor;
}

const FloorLabelIfNecessary: FunctionComponent<FloorLabelIfNecessaryProps> = (props) => {
    const { floors } = useContext(ShoppingPlannerContext);

    const floorsInSameFamily = floors.filter((floor) => floor.floorFamilyId === props.floor.floorFamilyId);

    if (floorsInSameFamily.length > 1) {
        return (
            <span className="shopping-planner-shopping-list__label shopping-planner-shopping-list__label--floor">
                {props.floor.floorLabel}
            </span>
        );
    } else {
        return null;
    }
};

interface BarCodeProps {
    ean: string;
}

export const BarCode: FunctionComponent<BarCodeProps> = (props) => {
    const ref = useRef<HTMLCanvasElement>(null);

    useEffect(() => {
        if (!props.ean) {
            return;
        }
        JsBarcode(ref.current, props.ean);
    }, [props.ean, ref.current]);

    return (
        <div className="shopping-planner-barcode">
            <canvas ref={ref}></canvas>
        </div>
    );
};

interface StoreFinderModalProps {
    onMarkerClick: (store: Store) => void;
    setStoreOverlay: Dispatch<SetStateAction<boolean>>;
}

const StoreFinderModal: FunctionComponent<StoreFinderModalProps> = (props) => {
    useEffect(() => {
        const closeOnEscapePress = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                props.setStoreOverlay(false);
            }
        };
        window.addEventListener('keydown', closeOnEscapePress, false);

        return () => {
            window.removeEventListener('keydown', closeOnEscapePress);
        };
    }, []);

    return (
        <div className="storefinder-overlay">
            <button type="button" className="storefinder-overlay__close" onClick={() => props.setStoreOverlay(false)}>
                <Icon icon="close" />
            </button>
            <h1>Standort wählen</h1>
            <div className="storefinder-overlay__inner">
                <StoreFinderComponent onMarkerClick={props.onMarkerClick} />
            </div>
        </div>
    );
};
